import * as turf from '@turf/turf';

export function getObservationPixelBucketSize() {
  return 44;
}

export const backendClusterShadowLayerId =
  'Observations_backend_clusters_shadow';
export const backendClusterCirclesLayerId = 'Observations_backend_clusters';
export const backendClusterCountLayerId = 'Observations_backend_cluster_count';
export const camtrapClusterShadowLayerId =
  'Observations_camtrap_clusters_shadow';
export const camtrapClusterLayerId = 'Observations_camtrap_clusters';
export const camtrapClusterCountLayerId = 'Observations_camtrap_cluster_count';
export const clusterSelectionSourceId = 'Observations_cluster_clicked';
export const clusterSelectionLayerId = 'Observations_cluster_clicked_ring';

// Shared radius expressions — used both when building layers and when applying
// the selected-state paint override via setPaintProperty.
const BACKEND_COUNT_EXPR = [
  'to-number',
  [
    'coalesce',
    ['get', 'point_count'],
    ['get', 'count'],
    ['get', 'Count'],
    ['get', 'Description'],
    ['get', 'description']
  ],
  0
];
export const BACKEND_RADIUS_EXPR = [
  'interpolate',
  ['linear'],
  BACKEND_COUNT_EXPR,
  1,
  14,
  99,
  16,
  100,
  20,
  999,
  24,
  1000,
  28,
  9999,
  34,
  10000,
  38,
  100000,
  46
];

const CAMTRAP_COUNT_EXPR = ['to-number', ['get', 'point_count'], 1];
export const CAMTRAP_RADIUS_EXPR = [
  'interpolate',
  ['linear'],
  CAMTRAP_COUNT_EXPR,
  1,
  12,
  99,
  14,
  100,
  18,
  1000,
  22
];

export function addBackendObservationClusterLayers(visibility = 'visible') {
  const isClusterFilter = [
    'all',
    ['==', ['get', 'EntityType'], 'cluster'],
    ['!=', ['get', 'overlap_cluster'], true]
  ];

  if (!this.map.getLayer(backendClusterShadowLayerId)) {
    this.map.addLayer({
      id: backendClusterShadowLayerId,
      type: 'circle',
      source: 'Observations',
      filter: isClusterFilter,
      layout: { visibility },
      paint: {
        'circle-color': 'rgba(0,0,0,0.18)',
        'circle-radius': ['+', BACKEND_RADIUS_EXPR, 5],
        'circle-blur': 0.6,
        'circle-translate': [0, 3],
        'circle-translate-anchor': 'viewport'
      }
    });
  }

  if (!this.map.getLayer(backendClusterCirclesLayerId)) {
    this.map.addLayer({
      id: backendClusterCirclesLayerId,
      type: 'circle',
      source: 'Observations',
      filter: isClusterFilter,
      layout: { visibility },
      paint: {
        'circle-color': '#FFAB00',
        'circle-radius': BACKEND_RADIUS_EXPR,
        'circle-stroke-color': 'rgba(255,255,255,0.9)',
        'circle-stroke-width': 1.5
      }
    });
  }

  if (!this.map.getLayer(backendClusterCountLayerId)) {
    this.map.addLayer({
      id: backendClusterCountLayerId,
      type: 'symbol',
      source: 'Observations',
      filter: isClusterFilter,
      layout: {
        visibility,
        'text-field': [
          'coalesce',
          ['get', 'point_count_abbreviated'],
          ['to-string', ['get', 'Description']],
          ''
        ],
        'text-font': ['literal', ['Open Sans Bold', 'Arial Unicode MS Bold']],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-size': 13
      },
      paint: {
        'text-color': '#ffffff'
      }
    });
  }
  if (this.map.getLayer(backendClusterCountLayerId)) {
    this.map.moveLayer(backendClusterCountLayerId);
  }

  // Register click handler once, after the layer exists.
  if (!this.backendClusterClickHandlerSet) {
    this.backendClusterClickHandlerSet = true;

    this.map.on('click', backendClusterCirclesLayerId, e => {
      if (!e.features || !e.features.length) return;
      const coords =
        e.features[0].geometry && e.features[0].geometry.coordinates;
      if (!coords) return;
      this.map.easeTo({
        center: coords,
        zoom: Math.min(this.map.getZoom() + 2, 18),
        duration: 400
      });
    });

    this.map.on('mouseenter', backendClusterCirclesLayerId, () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', backendClusterCirclesLayerId, () => {
      this.map.getCanvas().style.cursor = '';
    });
  }
}

export function addCamtrapClusterLayer(visibility = 'visible') {
  const camtrapFilter = [
    'all',
    ['==', ['get', 'EntityType'], 'cluster'],
    ['==', ['get', 'overlap_cluster'], true]
  ];

  if (!this.map.getLayer(camtrapClusterShadowLayerId)) {
    this.map.addLayer({
      id: camtrapClusterShadowLayerId,
      type: 'circle',
      source: 'Observations',
      filter: camtrapFilter,
      layout: { visibility },
      paint: {
        'circle-color': 'rgba(0,0,0,0.18)',
        'circle-radius': ['+', CAMTRAP_RADIUS_EXPR, 5],
        'circle-blur': 0.6,
        'circle-translate': [0, 3],
        'circle-translate-anchor': 'viewport'
      }
    });
  }

  if (!this.map.getLayer(camtrapClusterLayerId)) {
    this.map.addLayer({
      id: camtrapClusterLayerId,
      type: 'circle',
      source: 'Observations',
      filter: camtrapFilter,
      layout: { visibility },
      paint: {
        'circle-color': '#43a047',
        'circle-radius': CAMTRAP_RADIUS_EXPR,
        'circle-stroke-color': 'rgba(255,255,255,0.9)',
        'circle-stroke-width': 1.5
      }
    });
  } else {
    this.map.setPaintProperty(
      camtrapClusterLayerId,
      'circle-radius',
      CAMTRAP_RADIUS_EXPR
    );
  }

  if (!this.map.getLayer(camtrapClusterCountLayerId)) {
    this.map.addLayer({
      id: camtrapClusterCountLayerId,
      type: 'symbol',
      source: 'Observations',
      filter: camtrapFilter,
      layout: {
        visibility,
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['literal', ['Open Sans Bold', 'Arial Unicode MS Bold']],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-size': 13
      },
      paint: {
        'text-color': '#ffffff'
      }
    });
  }

  if (this.map.getLayer(camtrapClusterCountLayerId)) {
    this.map.moveLayer(camtrapClusterCountLayerId);
  }
}

export function rebuildObservationSourceAndLayers() {
  if (!this.map) return;

  const observationLayer = this.props.mapLayers.find(
    layer =>
      String(layer.projectId) === 'default' &&
      String(layer.name) === 'Observations'
  );
  if (!observationLayer) return;

  [
    camtrapClusterCountLayerId,
    camtrapClusterLayerId,
    camtrapClusterShadowLayerId,
    backendClusterCountLayerId,
    backendClusterCirclesLayerId,
    backendClusterShadowLayerId,
    'Observations_clicked',
    'Observations'
  ].forEach(layerId => {
    if (this.map.getLayer(layerId)) {
      this.map.removeLayer(layerId);
    }
  });

  if (this.map.getSource('Observations')) {
    this.map.removeSource('Observations');
  }

  this.map.addSource('Observations', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: []
    }
  });

  this.setObservationLayer(observationLayer);

  const visibility = this.props.selectedLayers.includes('Observations')
    ? 'visible'
    : 'none';
  this.setObservationMarkerLayerVisibility(visibility);
  const clusterVisibility =
    visibility === 'visible' && this.props.showObservationClusters
      ? 'visible'
      : 'none';
  this.addBackendObservationClusterLayers(clusterVisibility);
  this.addCamtrapClusterLayer(visibility);
  this.ensureClusterSelectionIndicator();
}

export function setObservationMarkerLayerVisibility(visibility) {
  ['Observations', 'Observations_clicked'].forEach(layerId => {
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  });
  const clusterVisibility =
    visibility === 'visible' && this.props.showObservationClusters
      ? 'visible'
      : 'none';
  [
    backendClusterShadowLayerId,
    backendClusterCirclesLayerId,
    backendClusterCountLayerId
  ].forEach(layerId => {
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(layerId, 'visibility', clusterVisibility);
    }
  });
  [
    camtrapClusterShadowLayerId,
    camtrapClusterLayerId,
    camtrapClusterCountLayerId
  ].forEach(layerId => {
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  });
}

// Radius stops scaled 25% larger than the base cluster sizes, used by the
// selection ring layer to make the selected cluster visibly bigger.
const SELECTION_RADIUS_EXPR = [
  'interpolate',
  ['linear'],
  [
    'to-number',
    ['coalesce', ['get', 'point_count'], ['get', 'count'], ['get', 'Count'], 1],
    1
  ],
  1,
  18,
  99,
  20,
  100,
  25,
  999,
  30,
  1000,
  35,
  10000,
  48,
  100000,
  58
];

export function ensureClusterSelectionIndicator() {
  if (!this.map) return;
  if (!this.map.getSource(clusterSelectionSourceId)) {
    this.map.addSource(clusterSelectionSourceId, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] }
    });
  }
  if (!this.map.getLayer(clusterSelectionLayerId)) {
    this.map.addLayer({
      id: clusterSelectionLayerId,
      type: 'circle',
      source: clusterSelectionSourceId,
      paint: {
        'circle-color': '#FFAB00',
        'circle-radius': SELECTION_RADIUS_EXPR,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 5,
        'circle-stroke-opacity': 1
      }
    });
  }
  // Keep ring above fill circles; count text layers will be moved on top next.
  if (this.map.getLayer(clusterSelectionLayerId)) {
    this.map.moveLayer(clusterSelectionLayerId);
  }
}

export function setClusterSelectionFilter(overlapKey, clusterFeature = null) {
  if (!this.map) return;
  if (!this.map.getSource(clusterSelectionSourceId)) {
    this.ensureClusterSelectionIndicator();
  }
  const source = this.map.getSource(clusterSelectionSourceId);
  if (!source) return;
  source.setData({
    type: 'FeatureCollection',
    features: overlapKey && clusterFeature ? [clusterFeature] : []
  });
  // Ensure count text stays above the ring after moveLayer above.
  [backendClusterCountLayerId, camtrapClusterCountLayerId].forEach(id => {
    if (this.map.getLayer(id)) this.map.moveLayer(id);
  });
}

export function isBackendClusterFeature(feature) {
  const props = feature && feature.properties;
  if (!props) return false;
  if (props.EntityType === 'cluster') return true;
  const name = String(props.Name || props.name || props.id || '');
  return /\[.*\]/.test(name);
}

export function getObservationFeatureCount(feature) {
  const props = feature && feature.properties;
  if (!props) return 1;
  if (this.isBackendClusterFeature(feature)) {
    const fromDescription = Number(props.Description);
    if (Number.isFinite(fromDescription) && fromDescription > 0)
      return fromDescription;
  }
  const rawCount = props.count !== undefined ? Number(props.count) : NaN;
  if (Number.isFinite(rawCount) && rawCount > 0) return rawCount;
  return 1;
}

const parseCountValue = val => {
  if (val === undefined || val === null) return NaN;
  const n = Number(
    String(val)
      .replace(/,/g, '')
      .trim()
  );
  return n;
};

export function normalizeObservationAggregateFeature(feature) {
  if (!feature || !feature.geometry || !feature.properties) return feature;

  const props = feature.properties;
  const isBackendCluster = this.isBackendClusterFeature(feature);
  const rawVal = isBackendCluster
    ? props.Description !== undefined
      ? props.Description
      : props.description !== undefined
      ? props.description
      : props.count !== undefined
      ? props.count
      : props.Count !== undefined
      ? props.Count
      : props.point_count
    : props.count !== undefined
    ? props.count
    : props.Count !== undefined
    ? props.Count
    : props.point_count;
  const rawCount = parseCountValue(rawVal);
  if (!Number.isFinite(rawCount) || rawCount <= 0) return feature;

  const countLabel =
    rawCount >= 1000 ? `${Math.round(rawCount / 1000)}k` : String(rawCount);
  const countProps = {
    count: rawCount,
    point_count: rawCount,
    point_count_abbreviated: countLabel
  };

  if (feature.geometry.type === 'Point') {
    return {
      ...feature,
      properties: {
        ...feature.properties,
        ...countProps
      }
    };
  }

  try {
    const centroid = turf.centroid(feature);
    if (
      centroid &&
      centroid.geometry &&
      Array.isArray(centroid.geometry.coordinates)
    ) {
      return {
        ...feature,
        geometry: {
          type: 'Point',
          coordinates: centroid.geometry.coordinates
        },
        properties: {
          ...feature.properties,
          ...countProps
        }
      };
    }
  } catch (error) {
    return feature;
  }

  return feature;
}

export function getObservationOverlapKey(coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  if (typeof coordinates[0] !== 'number' || typeof coordinates[1] !== 'number')
    return null;

  const lngBucket = Number(coordinates[0]).toFixed(5);
  const latBucket = Number(coordinates[1]).toFixed(5);
  return `geo:${lngBucket}:${latBucket}`;
}

export function buildObservationDataWithOverlapClusters(features) {
  const groups = {};
  const passThrough = [];

  (features || []).forEach(feature => {
    const geometry = feature && feature.geometry;
    const coordinates = geometry && geometry.coordinates;
    const isPoint = geometry && geometry.type === 'Point';
    const key = isPoint ? this.getObservationOverlapKey(coordinates) : null;

    if (!key) {
      passThrough.push(feature);
      return;
    }

    if (!groups[key]) groups[key] = [];
    groups[key].push(feature);
  });

  const processedFeatures = [...passThrough];
  const overlapGroups = {};
  const clusterMembers = {};
  const getFeatureCoords = feature => {
    const coords =
      feature && feature.geometry && Array.isArray(feature.geometry.coordinates)
        ? feature.geometry.coordinates
        : null;
    if (!coords || coords.length < 2) return null;
    if (typeof coords[0] !== 'number' || typeof coords[1] !== 'number')
      return null;
    return coords;
  };

  const candidates = Object.keys(groups)
    .map(key => {
      const group = groups[key];
      if (!group || group.length === 0) return null;

      let weightedLng = 0;
      let weightedLat = 0;
      let weightTotal = 0;
      let hasAggregatedMembers = false;
      const nonAggregatedMembers = [];

      group.forEach(item => {
        const props = (item && item.properties) || {};
        if (props.count !== undefined) {
          hasAggregatedMembers = true;
        } else {
          nonAggregatedMembers.push(item);
        }
        const coords = getFeatureCoords(item);
        if (!coords) return;
        const weight = this.getObservationFeatureCount(item);
        weightedLng += coords[0] * weight;
        weightedLat += coords[1] * weight;
        weightTotal += weight;
      });

      const totalCount = group.reduce(
        (sum, item) => sum + this.getObservationFeatureCount(item),
        0
      );
      const center =
        weightTotal > 0
          ? [weightedLng / weightTotal, weightedLat / weightTotal]
          : getFeatureCoords(group[0]);
      if (!center) return null;

      return {
        key,
        group,
        center,
        totalCount,
        hasAggregatedMembers,
        nonAggregatedMembers
      };
    })
    .filter(Boolean);

  candidates.forEach((cluster, index) => {
    const overlapKey = `merged:${index}`;
    clusterMembers[overlapKey] = cluster.group;

    const nonAggregatedCoordKeys = new Set(
      cluster.nonAggregatedMembers
        .map(item => getFeatureCoords(item))
        .filter(Boolean)
        .map(coords => `${coords[0].toFixed(5)}:${coords[1].toFixed(5)}`)
    );
    const hasOnlySameCoords =
      cluster.nonAggregatedMembers.length > 1 &&
      nonAggregatedCoordKeys.size <= 1;

    if (
      !cluster.hasAggregatedMembers &&
      cluster.totalCount === 1 &&
      cluster.group.length === 1
    ) {
      processedFeatures.push(cluster.group[0]);
      return;
    }

    if (hasOnlySameCoords) {
      overlapGroups[overlapKey] = cluster.nonAggregatedMembers;
    }

    processedFeatures.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: cluster.center
      },
      properties: {
        overlap_cluster: hasOnlySameCoords,
        overlap_key: overlapKey,
        count: cluster.totalCount,
        point_count: cluster.totalCount,
        point_count_abbreviated: String(cluster.totalCount),
        EntityType: 'cluster'
      }
    });
  });

  this.overlapObservationGroups = overlapGroups;
  this.observationClusterMembers = clusterMembers;
  return processedFeatures;
}
